import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Add, Delete, Remove } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import CommonPagination from "../../components/Table/CommonPagination";
import CustomTextField from "../../components/FormElements/CustomTextField";
import DateTimeRangePicker from "../../components/FormElements/DateTimeRangePicker";
import Spinner from "../../components/Spinner";
import ConfirmDeleteModal from "../ManageBadge/ConfirmDeleteModal";
import PageLayout from "../../components/PageLayout";
import useDebounce from "../ManageUsers/useDebounce";
import { showToast } from "../../store/slices/common";
import {
  createPoll,
  deletePoll,
  getPollHistory,
  listPolls,
  updatePoll,
} from "../../http/polls";
import { GIRL_GROUP_POLL_PROGRAM_ID } from "../../constants/programs";
import { usePermissions } from "../../hooks/usePermissions";

const limit = 10;

const STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Upcoming", value: "upcoming" },
  { label: "Past", value: "ended" },
  { label: "Inactive", value: "inactive" },
];

const statusColor = {
  active: "success",
  upcoming: "info",
  ended: "default",
  inactive: "warning",
};

const emptyOption = () => ({ label: "", description: "" });

const useStyles = makeStyles((theme) => ({
  root: {
    height: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    gap: "12px",
    flexWrap: "wrap",
  },
  button: {
    backgroundColor: "#142C5C !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
    borderColor: "#142C5C !important",
    "&:hover": {
      backgroundColor: "#0f2247 !important",
    },
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: "calc(100vh - 260px)",
    maxHeight: "calc(100vh - 260px)",
    boxShadow: "none !important",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    paddingBottom: "10px",
  },
  dialogContent: {
    maxWidth: "100%",
    padding: theme.spacing(2),
  },
}));

const formatDate = (value) =>
  value ? dayjs(value).format("MMM D, YYYY h:mm A") : null;

/** Live polls may have no start/end yet and still be active. */
const formatWindow = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  if (!start && !end) return "Open (no window set)";
  if (!start && end) return `Until ${end}`;
  if (start && !end) return `${start} → open`;
  return `${start} → ${end}`;
};

const formatWinner = (poll) => {
  if (!poll) return "—";
  if (poll.isTie && Array.isArray(poll.winners) && poll.winners.length > 0) {
    return `Tie: ${poll.winners.map((w) => w.label).join(", ")}`;
  }
  if (poll.winner?.label) return poll.winner.label;
  return "—";
};

const pollHasVotes = (poll) => {
  if (!poll) return false;
  if (typeof poll.totalVotes === "number" && poll.totalVotes > 0) return true;
  return (poll.options || []).some((opt) => (opt.voteCount || 0) > 0);
};

const isUndatedPoll = (poll) => !poll?.startDate && !poll?.endDate;

export default function ManagePolls() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isAdmin } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [polls, setPolls] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPolls, setTotalPolls] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const debouncedSearch = useDebounce(search);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);

  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState([emptyOption(), emptyOption()]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isActive, setIsActive] = useState(true);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [pollToDelete, setPollToDelete] = useState(null);

  const isPastView = statusFilter === "ended";
  const optionsLocked = useMemo(
    () => isEditMode && pollHasVotes(editingPoll),
    [isEditMode, editingPoll]
  );
  const editingIsUndated = isEditMode && isUndatedPoll(editingPoll);

  useEffect(() => {
    fetchPolls();
  }, [page, debouncedSearch, statusFilter]);

  const fetchPolls = async () => {
    try {
      setLoading(true);

      if (statusFilter === "ended") {
        // History endpoint: ended only, newest endDate first
        const response = await getPollHistory(GIRL_GROUP_POLL_PROGRAM_ID, {
          page: page + 1,
          limit,
        });
        let list = response?.data?.list || [];
        if (debouncedSearch) {
          const q = debouncedSearch.toLowerCase();
          list = list.filter((p) =>
            (p.question || "").toLowerCase().includes(q)
          );
        }
        setPolls(list);
        setTotalPolls(
          debouncedSearch
            ? list.length
            : response?.data?.resultCount ?? list.length
        );
        return;
      }

      const payload = {
        page: page + 1,
        limit,
        programId: GIRL_GROUP_POLL_PROGRAM_ID,
        search: debouncedSearch || undefined,
      };
      // Omit status to list all — undated live polls still count as active
      if (statusFilter) {
        payload.status = statusFilter;
      }

      const response = await listPolls(payload);
      setPolls(response?.data?.list || []);
      setTotalPolls(response?.data?.resultCount || 0);
    } catch (error) {
      console.error("Error fetching polls:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Failed to load naming polls. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setQuestion("");
    setOptions([emptyOption(), emptyOption()]);
    setStartDate(null);
    setEndDate(null);
    setIsActive(true);
    setEditingPoll(null);
    setIsEditMode(false);
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleOpenEdit = (poll) => {
    setIsEditMode(true);
    setEditingPoll(poll);
    setQuestion(poll.question || "");
    setOptions(
      (poll.options || []).length >= 2
        ? poll.options.map((opt) => ({
            label: opt.label || "",
            description: opt.description || "",
          }))
        : [emptyOption(), emptyOption()]
    );
    setStartDate(poll.startDate ? dayjs(poll.startDate) : null);
    setEndDate(poll.endDate ? dayjs(poll.endDate) : null);
    setIsActive(poll.isActive !== false);
    setOpenDialog(true);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const handleOptionChange = (index, field, value) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, [field]: value } : opt))
    );
  };

  const handleAddOption = () => {
    if (options.length >= 20) return;
    setOptions((prev) => [...prev, emptyOption()]);
  };

  const handleRemoveOption = (index) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) {
      dispatch(showToast({ color: "error", msg: "Question is required." }));
      return null;
    }
    if (trimmedQuestion.length > 280) {
      dispatch(
        showToast({
          color: "error",
          msg: "Question must be 280 characters or fewer.",
        })
      );
      return null;
    }

    // Create always needs a full window. Edit may set only endDate to close
    // an undated active poll, or leave dates unset.
    if (!isEditMode) {
      if (!startDate || !endDate) {
        dispatch(
          showToast({
            color: "error",
            msg: "Start and end dates are required to schedule a poll.",
          })
        );
        return null;
      }
    }

    if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate))) {
      dispatch(
        showToast({
          color: "error",
          msg: "End date must be after start date.",
        })
      );
      return null;
    }

    const cleanedOptions = options
      .map((opt) => ({
        label: (opt.label || "").trim(),
        description: (opt.description || "").trim() || undefined,
      }))
      .filter((opt) => opt.label);

    if (!optionsLocked) {
      if (cleanedOptions.length < 2) {
        dispatch(
          showToast({
            color: "error",
            msg: "Add at least 2 options with labels.",
          })
        );
        return null;
      }
      if (cleanedOptions.length > 20) {
        dispatch(
          showToast({
            color: "error",
            msg: "A poll can have at most 20 options.",
          })
        );
        return null;
      }
      const tooLong = cleanedOptions.find((opt) => opt.label.length > 80);
      if (tooLong) {
        dispatch(
          showToast({
            color: "error",
            msg: "Option labels must be 80 characters or fewer.",
          })
        );
        return null;
      }
    }

    return {
      question: trimmedQuestion,
      startDate: startDate ? dayjs(startDate).toISOString() : null,
      endDate: endDate ? dayjs(endDate).toISOString() : null,
      isActive,
      options: cleanedOptions,
    };
  };

  const handleSave = async () => {
    const form = validateForm();
    if (!form) return;

    try {
      setLoading(true);
      if (isEditMode && editingPoll?._id) {
        // Send only changed-relevant fields. Dates optional on edit so an
        // undated active poll can get just an endDate to close the window.
        const payload = {
          question: form.question,
          isActive: form.isActive,
        };
        if (form.startDate) payload.startDate = form.startDate;
        if (form.endDate) payload.endDate = form.endDate;
        if (!optionsLocked) {
          payload.options = form.options;
        }
        await updatePoll(editingPoll._id, payload);
        dispatch(
          showToast({ color: "success", msg: "Poll updated successfully." })
        );
      } else {
        await createPoll({
          programId: GIRL_GROUP_POLL_PROGRAM_ID,
          question: form.question,
          options: form.options,
          startDate: form.startDate,
          endDate: form.endDate,
          isActive: form.isActive,
        });
        dispatch(
          showToast({ color: "success", msg: "Poll scheduled successfully." })
        );
      }
      handleCloseDialog();
      fetchPolls();
    } catch (error) {
      console.error("Error saving poll:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Failed to save poll. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (poll) => {
    setPollToDelete(poll);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!pollToDelete?._id) return;
    try {
      setLoading(true);
      await deletePoll(pollToDelete._id);
      dispatch(
        showToast({ color: "success", msg: "Poll deleted successfully." })
      );
      setOpenDeleteModal(false);
      setPollToDelete(null);
      fetchPolls();
    } catch (error) {
      console.error("Error deleting poll:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Failed to delete poll. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const showWinnerColumn =
    isPastView ||
    polls.some((p) => p.status === "ended" || p.winner || p.isTie);

  return (
    <PageLayout
      title="Naming Polls"
      subtitle={
        isPastView
          ? "Ended polls with winners and vote tallies."
          : "Close the current naming vote or schedule the next options. Active polls may have no dates set yet."
      }
      loading={loading}
      actions={
        !isPastView ? (
          <Button
            className={classes.button}
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{ textTransform: "none", px: 2 }}
          >
            Schedule Poll
          </Button>
        ) : undefined
      }
    >
      <Box className={classes.root} sx={{ padding: "20px" }}>
      <ToastContainer />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        {STATUS_FILTERS.map((filter) => (
          <Chip
            key={filter.value || "all"}
            label={filter.label}
            clickable
            color={statusFilter === filter.value ? "primary" : "default"}
            variant={statusFilter === filter.value ? "filled" : "outlined"}
            onClick={() => handleStatusFilterChange(filter.value)}
            sx={{ textTransform: "capitalize" }}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <input
          placeholder="Search polls..."
          type="text"
          value={search}
          onChange={handleSearchChange}
          className="search-field-ui"
          style={{ height: "40px", width: "100%" }}
        />
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Question</TableCell>
              <TableCell>Window</TableCell>
              <TableCell>
                {isPastView ? "Vote breakdown" : "Options"}
              </TableCell>
              <TableCell>Votes</TableCell>
              {showWinnerColumn && <TableCell>Winner</TableCell>}
              {!isPastView && <TableCell>Status</TableCell>}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {polls.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    (showWinnerColumn ? 1 : 0) + (isPastView ? 5 : 6)
                  }
                  align="center"
                >
                  {isPastView
                    ? "No past naming polls yet."
                    : "No naming polls found."}
                </TableCell>
              </TableRow>
            ) : (
              polls.map((poll) => (
                <TableRow key={poll._id} hover>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {poll.question}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Typography variant="caption" display="block">
                      {formatWindow(poll.startDate, poll.endDate)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(poll.options || []).map((opt) => (
                        <Chip
                          key={opt._id || opt.label}
                          size="small"
                          label={
                            typeof opt.voteCount === "number"
                              ? `${opt.label} (${opt.voteCount})`
                              : opt.label
                          }
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{poll.totalVotes ?? 0}</TableCell>
                  {showWinnerColumn && (
                    <TableCell>{formatWinner(poll)}</TableCell>
                  )}
                  {!isPastView && (
                    <TableCell>
                      <Chip
                        size="small"
                        label={poll.status || "—"}
                        color={statusColor[poll.status] || "default"}
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {poll.status === "active" && (
                      <Button
                        size="small"
                        onClick={() => handleOpenEdit(poll)}
                        sx={{ textTransform: "none", mr: 1 }}
                      >
                        {isUndatedPoll(poll) || !poll.endDate
                          ? "Set end date"
                          : "Edit"}
                      </Button>
                    )}
                    {poll.status !== "active" && (
                      <Button
                        size="small"
                        onClick={() => handleOpenEdit(poll)}
                        sx={{ textTransform: "none", mr: 1 }}
                      >
                        Edit
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(poll)}
                        sx={{ textTransform: "none" }}
                      >
                        Delete
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.paginationContainer}>
        <CommonPagination
          rowsPerPage={limit}
          count={totalPolls}
          page={page}
          onPageChange={handlePageChange}
        />
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 9,
          "& .MuiDialog-container .MuiPaper-elevation": {
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center", pt: 3 }}>
          {isEditMode ? "Edit Naming Poll" : "Schedule Naming Poll"}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box mt={1}>
            <CustomTextField
              label="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              inputProps={{ maxLength: 280 }}
              multiline
              rows={2}
            />
          </Box>

          <Box mt={2}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#555", fontWeight: 600, mb: 1 }}
            >
              Options
            </Typography>
            {optionsLocked && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Options are locked because votes have already been cast. Schedule
                a new poll to change naming choices.
              </Typography>
            )}
            {options.map((opt, index) => (
              <Box
                key={`option-${index}`}
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  mb: 1.5,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <CustomTextField
                    label={`Option ${index + 1} label`}
                    value={opt.label}
                    onChange={(e) =>
                      handleOptionChange(index, "label", e.target.value)
                    }
                    inputProps={{
                      maxLength: 80,
                      readOnly: optionsLocked,
                    }}
                  />
                  <Box mt={1}>
                    <CustomTextField
                      label="Description (optional)"
                      value={opt.description}
                      onChange={(e) =>
                        handleOptionChange(index, "description", e.target.value)
                      }
                      inputProps={{ readOnly: optionsLocked }}
                    />
                  </Box>
                </Box>
                {!optionsLocked && (
                  <IconButton
                    aria-label="Remove option"
                    onClick={() => handleRemoveOption(index)}
                    disabled={options.length <= 2}
                    sx={{ mt: 1 }}
                  >
                    <Remove />
                  </IconButton>
                )}
              </Box>
            ))}
            {!optionsLocked && (
              <Button
                size="small"
                startIcon={<Add />}
                onClick={handleAddOption}
                disabled={options.length >= 20}
                sx={{ textTransform: "none" }}
              >
                Add option
              </Button>
            )}
          </Box>

          <Box mt={2}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#555", fontWeight: 600, mb: 0.5 }}
            >
              Vote window
            </Typography>
            {editingIsUndated && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                This live poll has no dates yet and is still active. Set an end
                date to close voting before scheduling the next options (windows
                cannot overlap).
              </Typography>
            )}
            {!isEditMode && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Use a future start date to schedule the next naming options.
                Ensure it begins after the current poll’s end date.
              </Typography>
            )}
            <DateTimeRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ color: "#555", fontWeight: 600 }}
              >
                Duration:
              </Typography>
              {[
                { label: "1 Week", days: 7 },
                { label: "2 Weeks", days: 14 },
              ].map((preset) => (
                <Button
                  key={preset.days}
                  size="small"
                  variant="outlined"
                  disabled={!startDate}
                  onClick={() => {
                    const start = dayjs(startDate);
                    if (start.isValid()) {
                      setEndDate(start.add(preset.days, "day"));
                    }
                  }}
                  sx={{
                    minWidth: 0,
                    px: 1.5,
                    py: 0.25,
                    fontSize: "12px",
                    textTransform: "none",
                    borderColor: "#bbb",
                    color: "#333",
                  }}
                >
                  {preset.label}
                </Button>
              ))}
              {isEditMode && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEndDate(dayjs())}
                  sx={{
                    minWidth: 0,
                    px: 1.5,
                    py: 0.25,
                    fontSize: "12px",
                    textTransform: "none",
                    borderColor: "#bbb",
                    color: "#333",
                    ml: 0.5,
                  }}
                >
                  End now
                </Button>
              )}
            </Box>
          </Box>

          <Box mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Active
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Uncheck to hide this poll from mobile immediately, even
                    inside its date window.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box
            className={classes.actionsContainer}
            sx={{
              justifyContent:
                isEditMode && isAdmin ? "space-between" : "flex-end",
            }}
          >
            {isEditMode && isAdmin && (
              <Button
                startIcon={<Delete />}
                sx={{
                  border: "1px solid #d32f2f",
                  color: "#d32f2f",
                  borderRadius: "6px",
                  fontSize: "14px",
                  textTransform: "none",
                }}
                onClick={() => {
                  handleCloseDialog();
                  handleDeleteClick(editingPoll);
                }}
              >
                Delete
              </Button>
            )}
            <Box>
              <Button
                onClick={handleCloseDialog}
                sx={{
                  border: "1px solid #888",
                  color: "#888",
                  borderRadius: "6px",
                  fontSize: "14px",
                  marginRight: "15px",
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button className={classes.button} onClick={handleSave}>
                {isEditMode ? "Update Poll" : "Schedule Poll"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {openDeleteModal && (
        <ConfirmDeleteModal
          poll={true}
          onAccept={handleConfirmDelete}
          onReject={() => {
            setOpenDeleteModal(false);
            setPollToDelete(null);
          }}
        />
      )}
      </Box>
    </PageLayout>
  );
}
